import React, { useCallback, useEffect, useMemo, useState, useRef, lazy, memo, createElement } from 'react';
import { ShoppingCart, X, Minus, Plus, AlertCircle, CheckCircle, Utensils, Search, Store, Coffee, Pizza, Sandwich, Beef, Carrot, IceCream, Leaf, Sparkles, ChevronRight, ChevronLeft, Timer, ArrowRight, Trash2, CreditCard, Building2, ShoppingBag, Clock, Heart, Info, Flame, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Importaciones de tu proyecto
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useOrders, useOrderActions } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { getSettings } from '../utils/settings';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { MenuItemOptionsModal } from '../components/MenuItemOptionsModal';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useBalance, formatMonth } from '../contexts/BalanceContext';
// --- 1. TYPES & INTERFACES ---
export interface MenuItem {
  id: string;
  originalId?: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
  selectedOptions?: any[];
  [key: string]: any;
}
export interface CartItem {
  item: MenuItem;
  quantity: number;
}
export interface CartState {
  [itemId: string]: number;
}
export type PaymentMethod = 'nomina';
// --- 2. HELPERS ---
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};
// Format price without trailing zeros: 950.00 → "950", 950.03 → "950.03"
const formatPrice = (price: number) => {
  return price.toLocaleString('es-DO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};
// Helper: Get effective price (without ITBIS if tax exempt, since all prices include ITBIS)
const getEffectivePrice = (price: number, taxExempt: boolean) => {
  return taxExempt ? price / 1.18 : price;
};
// Helper: Get display price for an item — company custom prices are shown as-is
const getItemDisplayPrice = (item: MenuItem, taxExempt: boolean) => {
  // If item has a company custom price (_basePrice is set), the custom price IS the final price
  if ((item as any)._basePrice !== undefined) return item.price;
  return getEffectivePrice(item.price, taxExempt);
};
// Generar sonido de éxito usando Web Audio API
// Pre-create AudioContext on first user interaction for mobile compatibility
let successAudioCtx: AudioContext | null = null;
const ensureAudioContext = () => {
  if (!successAudioCtx || successAudioCtx.state === 'closed') {
    successAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (successAudioCtx.state === 'suspended') {
    successAudioCtx.resume();
  }
  return successAudioCtx;
};
const playSuccessSound = () => {
  try {
    const audioCtx = ensureAudioContext();
    // Nota 1 - Do
    const osc1 = audioCtx.createOscillator();
    const gain1 = audioCtx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523.25, audioCtx.currentTime); // C5
    gain1.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    osc1.connect(gain1);
    gain1.connect(audioCtx.destination);
    osc1.start(audioCtx.currentTime);
    osc1.stop(audioCtx.currentTime + 0.3);
    // Nota 2 - Mi
    const osc2 = audioCtx.createOscillator();
    const gain2 = audioCtx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(659.25, audioCtx.currentTime + 0.15); // E5
    gain2.gain.setValueAtTime(0.3, audioCtx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.45);
    osc2.connect(gain2);
    gain2.connect(audioCtx.destination);
    osc2.start(audioCtx.currentTime + 0.15);
    osc2.stop(audioCtx.currentTime + 0.45);
    // Nota 3 - Sol (más alta y larga)
    const osc3 = audioCtx.createOscillator();
    const gain3 = audioCtx.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(783.99, audioCtx.currentTime + 0.3); // G5
    gain3.gain.setValueAtTime(0.35, audioCtx.currentTime + 0.3);
    gain3.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.8);
    osc3.connect(gain3);
    gain3.connect(audioCtx.destination);
    osc3.start(audioCtx.currentTime + 0.3);
    osc3.stop(audioCtx.currentTime + 0.8);
  } catch (e) {
    console.log('Audio not supported');
  }
};
const calculateSubsidy = (subtotal: number, subsidyString: string): number => {
  if (!subsidyString || subsidyString === '0' || subsidyString === 'RD$ 0') return 0;
  if (subsidyString.includes('%')) {
    const percentage = parseFloat(subsidyString.replace('%', ''));
    return subtotal * (percentage / 100);
  }
  if (subsidyString.includes('RD$')) {
    return parseFloat(subsidyString.replace('RD$', '').trim());
  }
  return 0;
};
const getCategoryIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('ensalada') || name.includes('salad')) return <Carrot size={18} data-id="element-2998" />;
  if (name.includes('carne') || name.includes('parrilla')) return <Beef size={18} data-id="element-2999" />;
  if (name.includes('pasta') || name.includes('italiana')) return <Utensils size={18} data-id="element-3000" />;
  if (name.includes('pizza')) return <Pizza size={18} data-id="element-3001" />;
  if (name.includes('postre') || name.includes('dulce')) return <IceCream size={18} data-id="element-3002" />;
  if (name.includes('cafe') || name.includes('bebida')) return <Coffee size={18} data-id="element-3003" />;
  if (name.includes('sandwich') || name.includes('hamburguesa')) return <Sandwich size={18} data-id="element-3004" />;
  if (name.includes('vegetariano') || name.includes('vegan')) return <Leaf size={18} data-id="element-3005" />;
  return <Utensils size={18} data-id="element-3006" />;
};
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 18) return 'Buenas tardes';
  return 'Buenas noches';
};
// --- 3. CUSTOM HOOKS ---
const useCountdown = (orderCutoffTime: string, orderStartTime?: string) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  useEffect(() => {
    const calculateTime = () => {
      // Always use Santo Domingo time regardless of device timezone
      const nowInSD = new Date(new Date().toLocaleString('en-US', {
        timeZone: 'America/Santo_Domingo'
      }));
      const [cutHours, cutMinutes] = orderCutoffTime.split(':').map(Number);
      const cutoff = new Date(nowInSD);
      cutoff.setHours(cutHours, cutMinutes, 0, 0);
      // Check if before start time
      if (orderStartTime) {
        const [startHours, startMinutes] = orderStartTime.split(':').map(Number);
        const start = new Date(nowInSD);
        start.setHours(startHours, startMinutes, 0, 0);
        if (nowInSD.getTime() < start.getTime()) {
          setIsOpen(false);
          setTimeLeft('No disponible');
          return;
        }
      }
      const diff = cutoff.getTime() - nowInSD.getTime();
      if (diff <= 0) {
        setIsOpen(false);
        setTimeLeft('00:00:00');
      } else {
        setIsOpen(true);
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
        const s = Math.floor(diff % (1000 * 60) / 1000);
        setTimeLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      }
    };
    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [orderCutoffTime, orderStartTime]);
  return {
    timeLeft,
    isOpen
  };
};
const useMenu = () => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyPricesApplied, setCompanyPricesApplied] = useState(false);
  const [companyCategoriesApplied, setCompanyCategoriesApplied] = useState(false);
  const [assignedCategoryList, setAssignedCategoryList] = useState<string[] | null>(null);
  // Store the master ordered list separately so company filtering never destroys it
  const masterCategoryOrderRef = useRef<string[]>([]);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const loadFromLocal = () => {
          const savedItems = localStorage.getItem('criket_menu_items');
          const savedCats = localStorage.getItem('criket_menu_categories');
          setItems(savedItems ? JSON.parse(savedItems) : []);
          const localCats = savedCats ? JSON.parse(savedCats) : [];
          masterCategoryOrderRef.current = localCats;
          setCategories(localCats);
        };
        if (isSupabaseConfigured()) {
          const [dbItems, dbCats] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories()]);
          let baseItems: MenuItem[] = [];
          if (dbItems.length > 0) baseItems = dbItems;else {
            loadFromLocal();
            baseItems = JSON.parse(localStorage.getItem('criket_menu_items') || '[]');
          }
          // Fetch saved category order from Supabase settings
          // This is the SINGLE SOURCE OF TRUTH for category ordering
          let orderedCats: string[] = dbCats;
          try {
            const savedOrder = await supabaseClient.getSettings('menu_category_order');
            if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
              // Use saved order, but only include categories that still exist
              const validOrdered = savedOrder.filter((c: string) => dbCats.includes(c));
              const newCats = dbCats.filter((c: string) => !savedOrder.includes(c));
              orderedCats = [...validOrdered, ...newCats];
            }
          } catch (e) {
            console.warn('Could not fetch category order from settings:', e);
          }
          // Store the master order — this NEVER gets overwritten by company filtering
          masterCategoryOrderRef.current = orderedCats;
          if (orderedCats.length > 0) setCategories(orderedCats);
          // Restaurar items personalizados (con opciones) desde localStorage
          try {
            const savedCustomItems = localStorage.getItem('criket_custom_items');
            if (savedCustomItems) {
              const customItems = JSON.parse(savedCustomItems) as MenuItem[];
              setItems([...baseItems, ...customItems]);
            } else {
              setItems(baseItems);
            }
          } catch {
            setItems(baseItems);
          }
        } else {
          loadFromLocal();
          try {
            const savedCustomItems = localStorage.getItem('criket_custom_items');
            if (savedCustomItems) {
              const customItems = JSON.parse(savedCustomItems) as MenuItem[];
              setItems(prev => [...prev, ...customItems]);
            }
          } catch {}
        }
        setCompanyPricesApplied(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  // Apply company-specific prices when company data is available
  const applyCompanyPrices = useCallback((companyPrices: Record<string, number>) => {
    if (!companyPrices || Object.keys(companyPrices).length === 0) return;
    if (companyPricesApplied) return;
    setItems(prev => prev.map(item => {
      // Only apply to base items (not custom/option variants)
      if (item.originalId) return item;
      const customPrice = companyPrices[item.id];
      if (customPrice !== undefined && customPrice > 0) {
        return {
          ...item,
          price: customPrice,
          _basePrice: item.price
        };
      }
      return item;
    }));
    setCompanyPricesApplied(true);
  }, [companyPricesApplied]);
  // Apply company-specific category filtering
  // DEFINITIVE FIX: Only set the filter list — NEVER modify categories state
  // The visibleCategories memo in the main component handles display filtering
  // while preserving the master order from Supabase settings
  const applyCompanyCategories = useCallback((assignedCategories: string[]) => {
    if (companyCategoriesApplied) return;
    if (!assignedCategories || assignedCategories.length === 0) {
      // No restriction — show all categories
      setAssignedCategoryList(null);
    } else {
      // Store assigned categories for filtering items in filteredItems
      setAssignedCategoryList(assignedCategories);
    }
    // Reset active category so it picks the first visible one
    setActiveCategory('');
    setCompanyCategoriesApplied(true);
  }, [companyCategoriesApplied]);
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Filter by assigned categories first (prevents flash of unauthorized items)
      if (assignedCategoryList && assignedCategoryList.length > 0) {
        if (!assignedCategoryList.includes(item.category)) return false;
      }
      const matchCat = !activeCategory || item.category === activeCategory;
      const matchSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchCat && matchSearch && !item.originalId;
    });
  }, [items, activeCategory, searchTerm, assignedCategoryList]);
  const addCustomItem = (newItem: MenuItem) => {
    setItems(prev => {
      const updated = [...prev, newItem];
      // Persistir custom items en localStorage
      const customItems = updated.filter(i => i.originalId);
      localStorage.setItem('criket_custom_items', JSON.stringify(customItems));
      return updated;
    });
  };
  const cleanCustomItems = () => {
    setItems(prev => prev.filter(i => !i.originalId));
    localStorage.removeItem('criket_custom_items');
  };
  // Set first category as default when categories load
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);
  return {
    items,
    categories,
    loading,
    activeCategory,
    setActiveCategory,
    searchTerm,
    setSearchTerm,
    filteredItems,
    addCustomItem,
    cleanCustomItems,
    applyCompanyPrices,
    applyCompanyCategories
  };
};
const useCart = (menuItems: MenuItem[]) => {
  const [cart, setCart] = useState<CartState>(() => {
    try {
      const saved = localStorage.getItem('criket_cart');
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      // If cart has a date stamp and it's from a previous day, clear it
      const today = new Date().toDateString();
      if (parsed._date && parsed._date !== today) {
        localStorage.removeItem('criket_cart');
        localStorage.removeItem('criket_custom_items');
        return {};
      }
      // Remove internal _date key before using as cart state
      const {
        _date,
        ...cartItems
      } = parsed;
      return cartItems;
    } catch {
      return {};
    }
  });
  useEffect(() => {
    // Save cart with today's date stamp so it auto-expires tomorrow
    const today = new Date().toDateString();
    localStorage.setItem('criket_cart', JSON.stringify({
      ...cart,
      _date: today
    }));
  }, [cart]);
  const addItem = useCallback((itemId: string) => {
    setCart(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1
    }));
  }, []);
  const removeItem = useCallback((itemId: string) => {
    setCart(prev => {
      const next = {
        ...prev
      };
      if (next[itemId] > 1) next[itemId]--;else delete next[itemId];
      return next;
    });
  }, []);
  const deleteItem = useCallback((itemId: string) => {
    setCart(prev => {
      const next = {
        ...prev
      };
      delete next[itemId];
      return next;
    });
  }, []);
  const clearCart = useCallback(() => setCart({}), []);
  const cartDetails = useMemo(() => {
    const items: CartItem[] = [];
    let subtotal = 0;
    let totalCount = 0;
    Object.entries(cart).forEach(([id, qty]) => {
      const item = menuItems.find(i => i.id === id);
      if (item) {
        items.push({
          item,
          quantity: qty
        });
        subtotal += item.price * qty;
        totalCount += qty;
      }
    });
    return {
      items,
      subtotal,
      totalCount
    };
  }, [cart, menuItems]);
  return {
    cart,
    addItem,
    removeItem,
    deleteItem,
    clearCart,
    cartDetails
  };
};
// --- 4. COMPONENTS VISUALES ---
const HeroSection = memo(({
  user,
  count,
  timer,
  isOpen,
  deliveryTime
}: any) => <div className="mb-0" data-id="element-3007">
      {/* Compact top bar - single row, no wrap */}
      <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3 mb-0" data-id="element-3008">
        <div className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 h-9 rounded-xl text-[10px] sm:text-xs font-bold uppercase tracking-wider border whitespace-nowrap ${isOpen ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800' : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'}`} data-id="element-3009">
          <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full shrink-0 ${isOpen ? 'bg-emerald-600 animate-pulse' : 'bg-red-600'}`} data-id="element-3010"></span>
          {isOpen ? 'Abierto' : 'Cerrado'}
          <span className="mx-0.5 sm:mx-1 text-gray-300 dark:text-gray-600" data-id="element-3011">
            |
          </span>
          <Clock size={12} className={`shrink-0 ${isOpen ? 'text-emerald-600' : 'text-red-500'}`} data-id="element-3012" />
          <span className={`font-mono font-bold text-[11px] sm:text-sm tabular-nums ${isOpen ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`} data-id="element-3013">
            {timer}
          </span>
        </div>
        {deliveryTime && <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 h-9 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300" data-id="element-3014">
            <ShoppingBag size={12} className="text-emerald-600 shrink-0" data-id="element-3015" />
            <span data-id="element-3016">
              Entrega: <strong className="font-mono" data-id="element-3017">{deliveryTime}</strong>
            </span>
          </div>}
      </div>

      {/* Closed banner */}
      {!isOpen && <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-3018">
          <AlertCircle size={18} className="text-red-600 shrink-0" data-id="element-3019" />
          <p className="text-sm text-red-800 dark:text-red-200 font-medium" data-id="element-3020">
            La cocina está cerrada. No se pueden realizar pedidos en este
            momento.
          </p>
        </div>}
    </div>);
// NEW: Compact Product Row for Mobile — REDESIGNED
const CompactProductRow = memo(({
  item,
  onAdd,
  onClick,
  isAdding,
  disabled,
  taxExempt = false,
  cartQty = 0
}: {
  item: MenuItem;
  onAdd: (i: MenuItem) => void;
  onClick: (i: MenuItem) => void;
  isAdding: boolean;
  disabled: boolean;
  taxExempt?: boolean;
  cartQty?: number;
}) => <motion.div layout className={`
        bg-white dark:bg-gray-900 rounded-2xl overflow-hidden
        border border-gray-100 dark:border-gray-800 shadow-sm
        active:scale-[0.985] transition-all duration-200
        ${disabled ? 'opacity-50 grayscale pointer-events-none' : ''}
      `} onClick={() => onClick(item)} data-id="element-3021">
      <div className="flex items-stretch gap-0" data-id="element-3022">
        {/* Image — taller, fills left side */}
        <div className="w-28 shrink-0 relative bg-gray-100 dark:bg-gray-800 overflow-hidden" data-id="element-3023">
          {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" style={{
        minHeight: '96px'
      }} data-id="element-3024" /> : <div className="w-full h-full min-h-[96px] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800" data-id="element-3025">
              <Utensils size={28} className="text-gray-300 dark:text-gray-600" data-id="element-3026" />
            </div>}
          {/* Cart quantity badge */}
          {cartQty > 0 && <div className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-md border border-white" data-id="element-3027">
              {cartQty}
            </div>}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 p-3 flex flex-col justify-between" data-id="element-3028">
          <div data-id="element-3029">
            <h3 className="font-bold text-sm text-gray-900 dark:text-white leading-snug line-clamp-2 mb-1" data-id="element-3030">
              {item.name}
            </h3>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 line-clamp-1 leading-relaxed" data-id="element-3031">
              {item.description}
            </p>
          </div>

          <div className="flex items-center justify-between mt-2.5 gap-2" data-id="element-3032">
            <span className="font-bold text-base text-gray-900 dark:text-white" data-id="element-3033">
              RD$ {formatPrice(getItemDisplayPrice(item, taxExempt))}
            </span>

            <button onClick={e => {
          e.stopPropagation();
          onAdd(item);
        }} disabled={disabled} className={`
                flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-bold
                transition-all duration-200 shrink-0 shadow-sm
                ${isAdding ? 'bg-emerald-500 text-white scale-95' : 'bg-gray-900 text-white dark:bg-white dark:text-black hover:bg-gray-700 dark:hover:bg-gray-100 active:scale-95'}
              `} data-id="element-3034">
              {isAdding ? <>
                  <CheckCircle size={13} data-id="element-3035" /> Listo
                </> : <>
                  <Plus size={13} data-id="element-3036" /> Agregar
                </>}
            </button>
          </div>
        </div>
      </div>
    </motion.div>);
// NEW: Product Detail Modal
const ProductDetailModal = ({
  item,
  isOpen,
  onClose,
  onAdd,
  taxExempt = false
}: {
  item: MenuItem | null;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (i: MenuItem) => void;
  taxExempt?: boolean;
}) => {
  if (!isOpen || !item) return null;
  return <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" data-id="element-3037">
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 300
    }} className="bg-white dark:bg-gray-900 w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3038">
        {/* Image Header */}
        <div className="relative h-72 bg-gray-100 dark:bg-gray-800 shrink-0" data-id="element-3039">
          {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-3040" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800" data-id="element-3041">
              <Utensils size={64} className="text-gray-300 dark:text-gray-600" data-id="element-3042" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3043"></div>
          <button onClick={onClose} className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-colors border border-white/10" data-id="element-3044">
            <X size={20} data-id="element-3045" />
          </button>

          {getItemDisplayPrice(item, taxExempt) < 250 && <span className="absolute top-4 left-4 bg-gray-900 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg border border-white/20" data-id="element-3046">
              OFERTA ESPECIAL
            </span>}

          <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3047">
            <span className="inline-block px-2.5 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-2 border border-white/10" data-id="element-3048">
              {item.category}
            </span>
            <h2 className="text-3xl font-bold text-white leading-tight shadow-black drop-shadow-md" data-id="element-3049">
              {item.name}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto bg-white dark:bg-gray-900 relative" data-id="element-3050">
          <div className="flex items-baseline justify-between mb-6" data-id="element-3051">
            <div className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-3052">
              RD$ {formatPrice(getItemDisplayPrice(item, taxExempt))}
            </div>
            <div className="text-sm text-gray-500 font-medium" data-id="element-3053">
              {taxExempt ? 'Precio (Exento ITBIS)' : 'Precio base'}
            </div>
          </div>

          <div className="prose prose-sm dark:prose-invert text-gray-600 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-3054">
            <p data-id="element-3055">{item.description}</p>
          </div>

          <Button onClick={() => {
          onAdd(item);
          onClose();
        }} fullWidth size="lg" className="h-14 text-base shadow-xl" data-id="element-3056">
            Agregar al Carrito
          </Button>
        </div>
      </motion.div>
    </div>;
};
const ProductCard = memo(({
  item,
  onAdd,
  isAdding,
  disabled,
  index = 0,
  taxExempt = false
}: {
  item: MenuItem;
  onAdd: (i: MenuItem) => void;
  isAdding: boolean;
  disabled: boolean;
  index?: number;
  taxExempt?: boolean;
}) => <div className={`
      group bg-white dark:bg-gray-900 rounded-2xl overflow-hidden flex flex-col h-full
      border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:shadow-gray-900/5 hover:-translate-y-1 transition-all duration-300
      ${disabled ? 'opacity-60 grayscale pointer-events-none' : ''}
  `} data-id="element-3057">
      <div className="h-52 overflow-hidden relative bg-gray-100 dark:bg-gray-800" data-id="element-3058">
        {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" data-id="element-3059" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800" data-id="element-3060">
            <Utensils size={48} className="text-gray-300 dark:text-gray-600" data-id="element-3061" />
          </div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-3062"></div>

        {getItemDisplayPrice(item, taxExempt) < 250 && <span className="absolute top-3 left-3 bg-gray-900 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg border border-white/20" data-id="element-3063">
            OFERTA
          </span>}

        <div className="absolute top-3 right-3 bg-white/90 dark:bg-black/80 text-gray-900 dark:text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm" data-id="element-3064">
          {item.category}
        </div>

        <div className="absolute bottom-3 right-3 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-xl shadow-lg border border-white/20" data-id="element-3065">
          RD$ {formatPrice(getItemDisplayPrice(item, taxExempt))}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1 relative" data-id="element-3066">
        <div className="flex-1 mb-5" data-id="element-3067">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 leading-tight" data-id="element-3068">
            {item.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed" data-id="element-3069">
            {item.description}
          </p>
        </div>

        <Button onClick={() => onAdd(item)} disabled={disabled} className={`
          w-full h-11 rounded-xl font-bold text-sm transition-colors border-none
          flex items-center justify-center gap-2 shadow-md
          ${isAdding ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-white dark:bg-white dark:text-black'}
        `} data-id="element-3070">
          {isAdding ? <>
              <CheckCircle size={18} data-id="element-3071" /> Listo
            </> : <>
              Agregar <Plus size={18} data-id="element-3072" />
            </>}
        </Button>
      </div>
    </div>);
const CheckoutDrawer = ({
  onClose,
  cartItems,
  totals,
  paymentMethod,
  setPaymentMethod,
  onCheckout,
  onUpdateQty,
  onRemove,
  taxExempt = false,
  isProcessing = false,
  currentBalance,
  applyBalance,
  onToggleBalance,
  subsidyEnabled = false,
  exceedsBalance = false
}: any) => {
  const isFullyCoveredByBalance = applyBalance && totals.balanceApplied > 0 && totals.total === 0;
  return <div className="fixed inset-0 z-50 flex justify-end" data-id="element-3073">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-black/70" onClick={onClose} data-id="element-3074"></motion.div>
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="relative w-full max-w-md bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col border-l border-gray-100 dark:border-gray-800" data-id="element-3075">
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900 z-10" data-id="element-3076">
          <div data-id="element-3077">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3078">
              Tu Pedido
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3079">
              Revisa tus platos antes de confirmar
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500" data-id="element-3080">
            <X size={24} data-id="element-3081" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50 dark:bg-black/20" data-id="element-3082">
          <AnimatePresence data-id="element-3083">
            {cartItems.map(({
            item,
            quantity
          }: any) => <motion.div key={item.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 flex gap-4 overflow-hidden shadow-sm" data-id="element-3084">
                {item.image ? <img src={item.image} alt="" className="w-20 h-20 rounded-xl object-cover bg-gray-200 shadow-sm" data-id="element-3085" /> : <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center shadow-sm shrink-0" data-id="element-3086">
                    <Utensils size={24} className="text-gray-400 dark:text-gray-500" data-id="element-3087" />
                  </div>}
                <div className="flex-1 flex flex-col justify-between min-w-0" data-id="element-3088">
                  <div className="flex justify-between items-start gap-2" data-id="element-3089">
                    <div className="flex-1 min-w-0" data-id="element-3090">
                      <h4 className="font-bold text-gray-900 dark:text-white text-sm line-clamp-1" data-id="element-3091">
                        {item.name}
                      </h4>
                      {item.selectedOptions && item.selectedOptions.length > 0 ? <div className="mt-1.5 space-y-1" data-id="element-3092">
                          {item.selectedOptions.map((opt: any, idx: number) => <div key={idx} className="flex items-center justify-between text-xs" data-id="element-3093">
                              <span className="text-gray-600 dark:text-gray-300 flex items-center gap-1.5" data-id="element-3094">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-3095"></span>
                                {opt.sectionName ? <span data-id="element-3096">
                                    <span className="text-gray-400 font-medium" data-id="element-3097">
                                      {opt.sectionName}:
                                    </span>{' '}
                                    {opt.optionName || opt.name}
                                  </span> : <span data-id="element-3098">{opt.optionName || opt.name}</span>}
                              </span>
                              {opt.priceModifier > 0 && <span className="text-emerald-600 dark:text-emerald-400 font-medium ml-2" data-id="element-3099">
                                  +RD${opt.priceModifier}
                                </span>}
                            </div>)}
                        </div> : <p className="text-[11px] text-gray-400 mt-1" data-id="element-3100">
                          Sin opciones adicionales
                        </p>}
                    </div>
                    <span className="font-bold text-sm text-gray-900 dark:text-white whitespace-nowrap" data-id="element-3101">
                      {formatCurrency(getItemDisplayPrice(item, taxExempt) * quantity)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3" data-id="element-3102">
                    <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 p-0.5" data-id="element-3103">
                      <button onClick={() => onUpdateQty(item.id, -1)} className="w-7 h-7 flex items-center justify-center hover:bg-white dark:hover:bg-gray-800 rounded-md transition-colors shadow-sm" data-id="element-3104">
                        <Minus size={14} data-id="element-3105" />
                      </button>
                      <span className="text-xs font-bold w-8 text-center" data-id="element-3106">
                        {quantity}
                      </span>
                      <button onClick={() => onUpdateQty(item.id, 1)} className="w-7 h-7 flex items-center justify-center hover:bg-white dark:hover:bg-gray-800 rounded-md transition-colors shadow-sm" data-id="element-3107">
                        <Plus size={14} data-id="element-3108" />
                      </button>
                    </div>
                    <button onClick={() => onRemove(item.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1" data-id="element-3109">
                      <Trash2 size={18} data-id="element-3110" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>

          {/* Balance Toggle — only shown if company has subsidyEnabled AND employee has balance */}
          {subsidyEnabled && currentBalance && currentBalance.currentBalance > 0 && <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm" data-id="element-3111">
                <div className="flex items-center justify-between" data-id="element-3112">
                  <div className="flex items-center gap-3" data-id="element-3113">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl" data-id="element-3114">
                      <Wallet size={18} className="text-emerald-600 dark:text-emerald-400" data-id="element-3115" />
                    </div>
                    <div data-id="element-3116">
                      <div className="flex items-center gap-2" data-id="element-3117">
                        <p className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-3118">
                          Usar Balance
                        </p>
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full" data-id="element-3119">
                          Obligatorio
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3120">
                        Disponible:{' '}
                        <span className="font-mono font-bold text-emerald-600" data-id="element-3121">
                          RD$ {currentBalance.currentBalance.toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>
                  {/* Locked toggle — always ON, not clickeable */}
                  <div className="w-11 h-6 rounded-full p-0.5 bg-emerald-500 cursor-not-allowed opacity-80" title="El balance es el único método de pago disponible" data-id="element-3122">
                    <div className="bg-white w-5 h-5 rounded-full shadow-sm transform translate-x-5" data-id="element-3123" />
                  </div>
                </div>
                {totals.balanceApplied > 0 && <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs" data-id="element-3124">
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium" data-id="element-3125">
                      Balance a aplicar
                    </span>
                    <span className="font-bold font-mono text-emerald-700 dark:text-emerald-300" data-id="element-3126">
                      - {formatCurrency(totals.balanceApplied)}
                    </span>
                  </div>}
              </div>}

          {/* Payment method — only shown when subsidy is NOT enabled */}
          {!subsidyEnabled && <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 mt-6 shadow-sm" data-id="element-3127">
              <h4 className="text-xs font-bold uppercase text-gray-400 mb-4 tracking-wider flex items-center gap-2" data-id="element-3128">
                <CreditCard size={14} data-id="element-3129" /> Método de Pago
              </h4>
              <div className="space-y-2" data-id="element-3130">
                <div className="w-full p-4 rounded-xl border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10 text-emerald-900 dark:text-emerald-100 flex items-center justify-between" data-id="element-3131">
                  <div className="flex items-center gap-3" data-id="element-3132">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600" data-id="element-3133">
                      <Building2 size={20} data-id="element-3134" />
                    </div>
                    <div data-id="element-3135">
                      <span className="font-bold text-sm block" data-id="element-3136">A Crédito</span>
                    </div>
                  </div>
                  <CheckCircle size={20} className="text-emerald-500" data-id="element-3137" />
                </div>
              </div>
            </div>}
          {/* END of scrollable content — NO more payment blocks below */}
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" data-id="element-3138">
          <div className="space-y-3 mb-6 text-sm" data-id="element-3139">
            <div className="flex justify-between text-gray-500" data-id="element-3140">
              <span data-id="element-3141">Subtotal</span>
              <span className="font-medium" data-id="element-3142">
                {formatCurrency(totals.subtotal)}
              </span>
            </div>
            <div className="flex justify-between text-gray-500" data-id="element-3143">
              <span data-id="element-3144">ITBIS (18%)</span>
              <span className="font-medium" data-id="element-3145">{formatCurrency(totals.tax)}</span>
            </div>
            {totals.subsidy > 0 && <div className="flex justify-between text-emerald-600 font-bold bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg" data-id="element-3146">
                <span data-id="element-3147">Subsidio Aplicado</span>
                <span data-id="element-3148">- {formatCurrency(totals.subsidy)}</span>
              </div>}
            {totals.balanceApplied > 0 && <div className="flex justify-between text-emerald-700 dark:text-emerald-300 font-bold bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg border border-emerald-200 dark:border-emerald-800" data-id="element-3149">
                <span className="flex items-center gap-1.5" data-id="element-3150">
                  <Wallet size={14} data-id="element-3151" /> Balance Aplicado
                </span>
                <span data-id="element-3152">- {formatCurrency(totals.balanceApplied)}</span>
              </div>}
            {/* Total line — shows balance consumed when fully covered, otherwise remaining to pay */}
            {isFullyCoveredByBalance ? <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-800 mt-2" data-id="element-3153">
                <span className="flex items-center gap-2 text-xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-3154">
                  <Wallet size={18} data-id="element-3155" /> Del Balance
                </span>
                <span className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-400" data-id="element-3156">
                  {formatCurrency(totals.balanceApplied)}
                </span>
              </div> : <div className="flex justify-between text-2xl font-bold text-gray-900 dark:text-white pt-4 border-t border-gray-100 dark:border-gray-800 mt-2" data-id="element-3157">
                <span data-id="element-3158">Total</span>
                <span data-id="element-3159">{formatCurrency(totals.total)}</span>
              </div>}
          </div>

          {/* Exceeds balance warning */}
          {exceedsBalance && currentBalance && <div className="flex items-start gap-3 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-3160">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-3161" />
              <div data-id="element-3162">
                <p className="text-sm font-bold text-red-700 dark:text-red-300" data-id="element-3163">
                  Balance insuficiente
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5" data-id="element-3164">
                  Tu pedido cuesta{' '}
                  <span className="font-bold font-mono" data-id="element-3165">
                    {formatCurrency(totals.subtotal + totals.tax)}
                  </span>{' '}
                  pero solo tienes{' '}
                  <span className="font-bold font-mono" data-id="element-3166">
                    RD$ {currentBalance.currentBalance.toFixed(2)}
                  </span>{' '}
                  disponible. Reduce tu pedido para continuar.
                </p>
              </div>
            </div>}

          <Button onClick={onCheckout} disabled={isProcessing || exceedsBalance} fullWidth className="h-14 text-base font-bold rounded-xl flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white shadow-xl shadow-gray-900/10 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-3167">
            {isProcessing ? <>
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" data-id="element-3168">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-3169" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3170" />
                </svg>
                Procesando...
              </> : exceedsBalance ? <>
                <AlertCircle size={20} data-id="element-3171" /> Balance insuficiente
              </> : <>
                Confirmar Orden <ArrowRight size={20} data-id="element-3172" />
              </>}
          </Button>
        </div>
      </motion.div>
    </div>;
};
const ProcessingOverlay = () => <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" data-id="element-3173">
    <motion.div initial={{
    scale: 0.9,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} className="bg-white dark:bg-gray-900 p-10 rounded-3xl shadow-2xl text-center max-w-sm border border-gray-100 dark:border-gray-800" data-id="element-3174">
      <div className="relative w-20 h-20 mx-auto mb-6" data-id="element-3175">
        <div className="absolute inset-0 border-4 border-gray-100 dark:border-gray-800 rounded-full" data-id="element-3176"></div>
        <div className="absolute inset-0 border-4 border-gray-900 dark:border-white rounded-full border-t-transparent animate-spin" data-id="element-3177"></div>
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-3178">
          <Utensils size={24} className="text-gray-900 dark:text-white" data-id="element-3179" />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3180">
        Procesando
      </h3>
      <p className="text-gray-500" data-id="element-3181">Enviando tu orden a la cocina...</p>
    </motion.div>
  </div>;
const SuccessModal = ({
  data,
  onClose
}: {
  data: any;
  onClose: () => void;
}) => <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-id="element-3182">
    <motion.div initial={{
    scale: 0.8,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden text-center p-8 border border-gray-100 dark:border-gray-800 relative" data-id="element-3183">
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-600" data-id="element-3184"></div>

      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      delay: 0.2,
      type: 'spring'
    }} className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-600 shadow-lg shadow-emerald-100" data-id="element-3185">
        <CheckCircle size={40} strokeWidth={2.5} data-id="element-3186" />
      </motion.div>

      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3187">
        ¡Orden Lista!
      </h2>
      <p className="text-gray-500 mb-6" data-id="element-3188">
        Tu pedido ha sido enviado exitosamente
      </p>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 mb-8 border border-gray-100 dark:border-gray-700" data-id="element-3189">
        <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1" data-id="element-3190">
          Número de Orden
        </p>
        <p className="text-4xl font-mono font-bold text-gray-900 dark:text-white tracking-widest" data-id="element-3191">
          #{data.orderNo}
        </p>
      </div>

      <Button onClick={onClose} fullWidth className="bg-gray-900 hover:bg-black text-white rounded-xl h-14 text-base shadow-lg" data-id="element-3192">
        Ver Mis Pedidos
      </Button>
    </motion.div>
  </div>;
// --- 5. MAIN COMPONENT ---
export function EmployeeOrderPage() {
  useDocumentTitle('Hacer Pedido');
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  // Use useOrderActions instead of useOrders — this prevents the entire page
  // from re-rendering every time any order changes in real-time
  const {
    addOrder
  } = useOrderActions();
  const {
    showToast
  } = useToast();
  const {
    currentBalance,
    hasBalance,
    consumeBalance,
    loading: balanceLoading,
    refreshBalance
  } = useBalance();
  // Balance application state
  const [applyBalance, setApplyBalance] = useState(false);
  useEffect(() => {
    if (hasBalance) setApplyBalance(true);else setApplyBalance(false);
  }, [hasBalance]);
  // Warm up AudioContext on first user interaction (required for mobile)
  useEffect(() => {
    const warmUp = () => {
      ensureAudioContext();
      document.removeEventListener('touchstart', warmUp);
      document.removeEventListener('click', warmUp);
    };
    document.addEventListener('touchstart', warmUp, {
      once: true
    });
    document.addEventListener('click', warmUp, {
      once: true
    });
    return () => {
      document.removeEventListener('touchstart', warmUp);
      document.removeEventListener('click', warmUp);
    };
  }, []);
  const {
    items,
    loading,
    categories,
    activeCategory,
    setActiveCategory,
    searchTerm,
    setSearchTerm,
    filteredItems,
    addCustomItem,
    cleanCustomItems,
    applyCompanyPrices,
    applyCompanyCategories
  } = useMenu();
  const {
    cart,
    addItem,
    removeItem,
    deleteItem,
    clearCart,
    cartDetails
  } = useCart(items);
  const settings = getSettings();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const [selectedItemForOptions, setSelectedItemForOptions] = useState<MenuItem | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('nomina');
  const [confirmedData, setConfirmedData] = useState<any>(null);
  // Track whether company data has been resolved (to avoid flash of all items)
  const [companyResolved, setCompanyResolved] = useState(false);
  // Store pending company prices to re-apply after menu loads (race condition fix)
  const pendingPricesRef = useRef<Record<string, number>>({});
  // Prevent double-submit
  const isSubmittingRef = useRef(false);
  // Category scroll ref for arrow navigation
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollButtons = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, {
      passive: true
    });
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, [updateScrollButtons, categories]);
  const scrollCategories = useCallback((dir: 'left' | 'right') => {
    const el = categoryScrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'right' ? 120 : -120,
      behavior: 'smooth'
    });
  }, []);
  // Re-apply company prices once menu finishes loading (fixes race condition)
  useEffect(() => {
    if (!loading && Object.keys(pendingPricesRef.current).length > 0) {
      applyCompanyPrices(pendingPricesRef.current);
    }
  }, [loading]);
  // Resolved employee company/branch - fetched directly from DB
  const [resolvedCompany, setResolvedCompany] = useState<string>(user?.company || '');
  const [resolvedBranch, setResolvedBranch] = useState<string>(user?.branch || '');
  const [resolvedCompanyData, setResolvedCompanyData] = useState<any>(null);
  // NEW: State for detail modal
  const [selectedDetailItem, setSelectedDetailItem] = useState<MenuItem | null>(null);
  // Use company-specific cutoff time, fallback to global settings
  const effectiveCutoffTime = resolvedCompanyData?.orderCutoffTime || settings.orderCutoffTime || '11:00';
  const effectiveStartTime = resolvedCompanyData?.deliveryStartTime || undefined;
  const {
    timeLeft,
    isOpen: canOrder
  } = useCountdown(effectiveCutoffTime, effectiveStartTime);
  // On mount: resolve employee's company/branch directly from Supabase
  // + Real-time subscription for instant updates + polling as fallback
  useEffect(() => {
    if (!user || user.role !== 'employee' || !isSupabaseConfigured()) {
      // If not an employee or no Supabase, mark as resolved immediately
      setCompanyResolved(true);
      return;
    }
    let subscription: any = null;
    // Refs to track last resolved values and avoid unnecessary state updates
    const lastCompanyRef = {
      name: '',
      branch: '',
      dataId: ''
    };
    const resolveEmployeeCompany = async () => {
      try {
        const companies = await supabaseClient.getCompanies();
        if (companies.length > 0) {
          localStorage.setItem('criket_companies', JSON.stringify(companies));
        }
        const employees = await supabaseClient.getEmployees();
        const emp = employees.find((e: any) => e.id === user.id || e.id === user.employeeId);
        if (emp && emp.company_id) {
          const company = companies.find((c: any) => c.id === emp.company_id);
          const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
          if (company) {
            const newName = company.name;
            const newBranch = branch?.name || 'Principal';
            const newDataId = String(company.id);
            // Only update state if values actually changed
            if (lastCompanyRef.name !== newName) {
              lastCompanyRef.name = newName;
              setResolvedCompany(newName);
            }
            if (lastCompanyRef.branch !== newBranch) {
              lastCompanyRef.branch = newBranch;
              setResolvedBranch(newBranch);
            }
            if (lastCompanyRef.dataId !== newDataId) {
              lastCompanyRef.dataId = newDataId;
              setResolvedCompanyData(company);
              // Apply company-specific prices only when company actually changes
              if (company.companyPrices && Object.keys(company.companyPrices).length > 0) {
                pendingPricesRef.current = company.companyPrices;
                applyCompanyPrices(company.companyPrices);
              }
              applyCompanyCategories(company.assignedCategories || []);
            }
          }
        }
        // Mark company as resolved after first successful fetch
        setCompanyResolved(true);
      } catch (e) {
        console.error('Error resolving employee company:', e);
        setResolvedCompany(user.company || 'N/A');
        setResolvedBranch(user.branch || 'N/A');
        setCompanyResolved(true);
      }
    };
    // Initial fetch
    resolveEmployeeCompany();
    // Real-time subscription: instant updates when admin changes company settings
    try {
      subscription = supabaseClient.subscribeToCompanies(() => {
        resolveEmployeeCompany();
      });
    } catch (e) {
      console.log('Realtime not available, using polling only');
    }
    // Polling fallback every 60s (in case realtime is not enabled)
    const pollInterval = setInterval(resolveEmployeeCompany, 60000);
    return () => {
      clearInterval(pollInterval);
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {}
      }
    };
  }, [user?.id]);
  // Combined loading: wait for both menu AND company data
  const isFullyLoaded = !loading && companyResolved;
  // Use resolved company data for tax/subsidy calculations
  const companies = getCompanies();
  const userCompany = resolvedCompanyData || companies.find(c => c.name === resolvedCompany);
  // CRITICAL FIX: Secondary filter using company data directly
  // This guarantees items are filtered by assigned categories regardless of useMenu internal state
  const visibleItems = useMemo(() => {
    const assignedCats = resolvedCompanyData?.assignedCategories;
    if (!assignedCats || assignedCats.length === 0) return filteredItems;
    return filteredItems.filter(item => assignedCats.includes(item.category));
  }, [filteredItems, resolvedCompanyData]);
  // CRITICAL FIX: Also filter CATEGORIES shown in sidebar/pills by company assignment
  // This prevents showing categories the company shouldn't see
  const visibleCategories = useMemo(() => {
    const assignedCats = resolvedCompanyData?.assignedCategories;
    if (!assignedCats || assignedCats.length === 0) return categories;
    return categories.filter(cat => assignedCats.includes(cat));
  }, [categories, resolvedCompanyData]);
  // Auto-select first visible category when company data loads
  useEffect(() => {
    if (visibleCategories.length > 0 && activeCategory) {
      // If current active category is not in visible list, reset to first
      if (!visibleCategories.includes(activeCategory)) {
        setActiveCategory(visibleCategories[0]);
      }
    } else if (visibleCategories.length > 0 && !activeCategory) {
      setActiveCategory(visibleCategories[0]);
    }
  }, [visibleCategories, activeCategory, setActiveCategory]);
  const isTaxExempt = userCompany?.taxExempt || false;
  const subsidyEnabled = userCompany?.subsidyEnabled === true;
  const subsidyAmount = calculateSubsidy(cartDetails.subtotal, userCompany?.subsidy || '0');
  // Calculate totals — the displayed price IS the total (includes ITBIS)
  // ITBIS is extracted FROM the total, and subtotal = total - ITBIS
  const {
    baseSubtotal,
    taxAmount,
    grandTotal
  } = useMemo(() => {
    let total = 0;
    for (const {
      item,
      quantity
    } of cartDetails.items) {
      // The display price is the TOTAL the customer pays per item
      total += getItemDisplayPrice(item, isTaxExempt) * quantity;
    }
    // Extract ITBIS from the total (not added on top)
    let tax = 0;
    if (!isTaxExempt) {
      // ITBIS is included in the price — extract it: tax = total - (total / 1.18)
      tax = total - total / 1.18;
    }
    // Subtotal = total - ITBIS
    const subtotal = total - tax;
    return {
      baseSubtotal: subtotal,
      taxAmount: tax,
      grandTotal: total
    };
  }, [cartDetails.items, isTaxExempt]);
  const afterSubsidy = grandTotal - subsidyAmount;
  // Only apply balance if company has subsidyEnabled
  const balanceApplied = subsidyEnabled && applyBalance && currentBalance ? Math.min(currentBalance.currentBalance, Math.max(0, afterSubsidy)) : 0;
  const finalTotal = Math.max(0, afterSubsidy - balanceApplied);
  // Block order if total exceeds available balance (when balance mode is active)
  // FIX: Remove applyBalance dependency — when subsidyEnabled, balance is REQUIRED
  // If employee has a balance record and it's insufficient, always block
  const exceedsBalance = subsidyEnabled && currentBalance !== null && afterSubsidy > currentBalance.currentBalance;
  // Helper for date formatting
  const getFormattedDate = () => {
    return new Date().toLocaleDateString('es-DO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const handleAddToCart = useCallback((item: MenuItem) => {
    if (!canOrder) return;
    // Close detail modal first, then open options modal
    setSelectedDetailItem(null);
    setSelectedItemForOptions(item);
  }, [canOrder]);
  const onConfirmOptions = (options: any[], price: number) => {
    if (!selectedItemForOptions) return;
    setLastAddedId(selectedItemForOptions.id);
    setTimeout(() => setLastAddedId(null), 1000);
    const signature = JSON.stringify(options.sort((a, b) => a.optionName.localeCompare(b.optionName)));
    const existing = items.find(i => i.originalId === selectedItemForOptions.id && JSON.stringify(i.selectedOptions) === signature);
    if (existing) {
      addItem(existing.id);
    } else {
      const customId = `${selectedItemForOptions.id}-c-${Date.now()}`;
      const customItem = {
        ...selectedItemForOptions,
        id: customId,
        originalId: selectedItemForOptions.id,
        price,
        selectedOptions: options
      };
      addCustomItem(customItem);
      addItem(customId);
    }
    setSelectedItemForOptions(null);
  };
  const handleCheckoutProcess = async () => {
    if (!user || !canOrder) return;
    if (isSubmittingRef.current) return;
    // LOCK IMMEDIATELY — before any async work to prevent double-submit
    isSubmittingRef.current = true;
    setIsProcessing(true);
    try {
      // CRITICAL SAFETY CHECK: Fetch fresh balance from DB right before submitting
      // This prevents the multi-tab race condition where stale state allows an order
      // through even though balance was already spent in another tab/session
      if (subsidyEnabled && user.employeeId) {
        try {
          const freshBalance = await supabaseClient.getEmployeeBalance(user.employeeId, new Date().getMonth() + 1, new Date().getFullYear());
          if (freshBalance !== null && afterSubsidy > freshBalance.currentBalance) {
            // Balance changed — update UI and block
            await refreshBalance();
            showToast('error', 'Balance insuficiente', `Tu saldo disponible es RD$ ${freshBalance.currentBalance.toFixed(2)}. Reduce tu pedido para continuar.`);
            return;
          }
        } catch (e) {
          // If the fresh check fails, fall through to the cached check below
          console.warn('[Balance] Fresh check failed, using cached value:', e);
        }
      }
      // Block if order exceeds balance (cached check as secondary guard)
      if (exceedsBalance) {
        showToast('error', 'Balance insuficiente', 'El total del pedido supera tu balance disponible.');
        return;
      }
      const orderCompany = resolvedCompany || user.company || 'N/A';
      const orderBranch = resolvedBranch || user.branch || 'N/A';
      const createdOrderNos: string[] = [];
      for (const cartItem of cartDetails.items) {
        const {
          item,
          quantity
        } = cartItem;
        let productDesc = item.name;
        if (item.selectedOptions && item.selectedOptions.length > 0) {
          const optionNames = item.selectedOptions.map((opt: any) => opt.optionName).join(', ');
          productDesc += ' (' + optionNames + ')';
        }
        for (let i = 0; i < quantity; i++) {
          const effectivePrice = getItemDisplayPrice(item, isTaxExempt);
          const orderPayload = {
            deliveryDate: new Date().toLocaleString('es-DO', {
              timeZone: 'America/Santo_Domingo'
            }),
            type: 'Envío',
            name: user.name,
            company: orderCompany,
            branch: orderBranch,
            product: productDesc,
            quantity: 1,
            catalogType: 'Almuerzo',
            subtotal: effectivePrice.toFixed(2),
            total: effectivePrice.toFixed(2),
            taxExempt: isTaxExempt,
            employeeId: user.id,
            paymentMethod: 'Nómina'
          };
          const orderNo = await addOrder(orderPayload);
          createdOrderNos.push(orderNo);
        }
      }
      // Consume balance after orders are created
      if (balanceApplied > 0) {
        try {
          await consumeBalance(balanceApplied, user.name);
        } catch (e) {
          console.error('[Balance] Error consuming balance (non-critical):', e);
        }
      }
      const displayOrderNo = createdOrderNos[0] || '1';
      setConfirmedData({
        orderNo: displayOrderNo,
        items: [...cartDetails.items],
        details: cartDetails,
        total: finalTotal.toFixed(2),
        tax: taxAmount,
        subsidy: subsidyAmount,
        balanceApplied
      });
      clearCart();
      cleanCustomItems();
      setShowCheckout(false);
      setShowConfirmation(true);
      playSuccessSound();
    } catch (e) {
      showToast('error', 'Error', 'Fallo al procesar');
    } finally {
      setIsProcessing(false);
      isSubmittingRef.current = false;
    }
  };
  return <div className="bg-gray-50 dark:bg-[#050505] font-sans text-gray-900 dark:text-gray-100 pb-24" data-id="element-3193">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 sm:py-4" data-id="element-3194">
        {/* Balance Card */}
        {!balanceLoading && subsidyEnabled && currentBalance && <div className="mb-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden" data-id="element-3195">
            {/* Accent bar */}
            <div className="h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400" data-id="element-3196" />
            <div className="p-4" data-id="element-3197">
              <div className="flex items-center justify-between mb-4" data-id="element-3198">
                <div className="flex items-center gap-2.5" data-id="element-3199">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl" data-id="element-3200">
                    <Wallet size={16} className="text-emerald-600 dark:text-emerald-400" data-id="element-3201" />
                  </div>
                  <div data-id="element-3202">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight" data-id="element-3203">
                      Mi Balance del Mes
                    </h3>
                    <p className="text-[11px] text-gray-400 leading-tight" data-id="element-3204">
                      {formatMonth(currentBalance.month, currentBalance.year)}
                    </p>
                  </div>
                </div>
                {/* Circular progress */}
                <div className="relative w-14 h-14 shrink-0" data-id="element-3205">
                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56" data-id="element-3206">
                    <circle cx="28" cy="28" r="22" fill="none" stroke="#f0fdf4" strokeWidth="5" data-id="element-3207" />
                    <circle cx="28" cy="28" r="22" fill="none" stroke={currentBalance.currentBalance > currentBalance.assignedAmount * 0.2 ? '#10b981' : '#f97316'} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 22}`} strokeDashoffset={`${2 * Math.PI * 22 * (1 - Math.min(1, currentBalance.currentBalance / currentBalance.assignedAmount))}`} className="transition-all duration-700" data-id="element-3208" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-3209">
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300" data-id="element-3210">
                      {currentBalance.assignedAmount > 0 ? Math.round(currentBalance.currentBalance / currentBalance.assignedAmount * 100) : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3" data-id="element-3211">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3" data-id="element-3212">
                  <p className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1" data-id="element-3213">
                    Disponible
                  </p>
                  <p className={`text-sm font-bold font-mono tabular-nums ${currentBalance.currentBalance > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-500'}`} data-id="element-3214">
                    RD$ {currentBalance.currentBalance.toFixed(0)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3" data-id="element-3215">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1" data-id="element-3216">
                    Asignado
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums text-gray-900 dark:text-white" data-id="element-3217">
                    RD$ {currentBalance.assignedAmount.toFixed(0)}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3" data-id="element-3218">
                  <p className="text-[9px] font-bold text-orange-500 uppercase tracking-widest mb-1" data-id="element-3219">
                    Consumido
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums text-orange-600 dark:text-orange-400" data-id="element-3220">
                    RD${' '}
                    {(currentBalance.assignedAmount - currentBalance.currentBalance).toFixed(0)}
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {/* Desktop: Search + Badges in one row ABOVE the grid */}
        <div className="hidden lg:flex items-center gap-6 mb-4" data-id="element-3221">
          <div className="bg-white dark:bg-gray-800/50 rounded-2xl p-4 flex items-center border border-gray-100 dark:border-gray-700 shadow-sm focus-within:ring-2 ring-gray-900/10 transition-all w-[260px] shrink-0" data-id="element-3222">
            <Search className="text-gray-400 mr-3" size={20} data-id="element-3223" />
            <input className="w-full bg-transparent border-none outline-none text-sm font-medium placeholder:text-gray-400" placeholder="Buscar plato..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3224" />
            {searchTerm && <button onClick={() => setSearchTerm('')} data-id="element-3225">
                <X size={16} className="text-gray-400 hover:text-gray-600" data-id="element-3226" />
              </button>}
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0" data-id="element-3227">
            <div className={`flex items-center gap-2 px-3 h-9 rounded-xl text-xs font-bold uppercase tracking-wider border whitespace-nowrap ${canOrder ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800' : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'}`} data-id="element-3228">
              <span className={`w-2 h-2 rounded-full ${canOrder ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} data-id="element-3229"></span>
              {canOrder ? 'Abierto' : 'Cerrado'}
              <span className="mx-1 text-gray-300 dark:text-gray-600" data-id="element-3230">|</span>
              <Clock size={12} className={`shrink-0 ${canOrder ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-3231" />
              <span className="font-mono font-bold tabular-nums text-sm" data-id="element-3232">
                {timeLeft}
              </span>
            </div>
            {resolvedCompanyData?.deliveryEndTime && <div className="flex items-center gap-2 px-3 h-9 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl whitespace-nowrap text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300" data-id="element-3233">
                <ShoppingBag size={12} className="text-emerald-600 shrink-0" data-id="element-3234" />
                <span data-id="element-3235">
                  Entrega:{' '}
                  <strong className="font-mono" data-id="element-3236">
                    {resolvedCompanyData.deliveryEndTime}
                  </strong>
                </span>
              </div>}
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[260px_1fr] gap-x-10 items-start" data-id="element-3237">
          {/* Sidebar: Categories only (search moved above) */}
          <aside className="hidden lg:block sticky top-24 space-y-8 mt-4" data-id="element-3238">
            <nav className="space-y-2" data-id="element-3239">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 mb-3 flex items-center gap-2" data-id="element-3240">
                <Utensils size={12} data-id="element-3241" /> Categorías
              </h3>
              {visibleCategories.map(cat => {
              const isActive = activeCategory === cat;
              return <button key={cat} onClick={() => setActiveCategory(cat)} className={`
                           relative w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 group
                           ${isActive ? 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800' : 'text-gray-600 hover:bg-gray-50 hover:shadow-sm dark:text-gray-400 dark:hover:bg-gray-800'}
                        `} data-id="element-3242">
                    {isActive && <motion.div layoutId="activeCategory" className="absolute left-0 top-0 bottom-0 w-1 bg-gray-900 dark:bg-white rounded-l-xl" data-id="element-3243" />}
                    <span className={`transition-colors ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 group-hover:text-gray-600'}`} data-id="element-3244">
                      {getCategoryIcon(cat)}
                    </span>
                    {cat}
                    {isActive && <ChevronRight size={16} className="ml-auto text-gray-900 dark:text-white" data-id="element-3245" />}
                  </button>;
            })}
            </nav>
          </aside>

          <main className="mt-0 lg:mt-0" data-id="element-3246">
            {!canOrder && <div className="flex items-center gap-3 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-3247">
                <AlertCircle size={18} className="text-red-600 shrink-0" data-id="element-3248" />
                <p className="text-sm text-red-800 dark:text-red-200 font-medium" data-id="element-3249">
                  La cocina está cerrada. No se pueden realizar pedidos en este
                  momento.
                </p>
              </div>}

            {/* Mobile: Search → Badges → Categories */}
            <div className="lg:hidden mb-5 space-y-3" data-id="element-3250">
              {/* Row 1: Status Badges */}
              <div className="flex items-center justify-between gap-2" data-id="element-3251">
                <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border shadow-sm ${canOrder ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800' : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'}`} data-id="element-3252">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${canOrder ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} data-id="element-3253"></span>
                  <span className="uppercase tracking-wider font-bold" data-id="element-3254">
                    {canOrder ? 'Abierto' : 'Cerrado'}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600 mx-1" data-id="element-3255">
                    |
                  </span>
                  <Clock size={11} className={`shrink-0 ${canOrder ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-3256" />
                  <span className="font-mono font-bold tabular-nums" data-id="element-3257">
                    {timeLeft}
                  </span>
                </div>
                {resolvedCompanyData?.deliveryEndTime && <div className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm text-xs font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap" data-id="element-3258">
                    <ShoppingBag size={11} className="text-gray-400 shrink-0" data-id="element-3259" />
                    <span data-id="element-3260">
                      Entrega:{' '}
                      <strong className="font-mono text-gray-900 dark:text-white" data-id="element-3261">
                        {resolvedCompanyData.deliveryEndTime}
                      </strong>
                    </span>
                  </div>}
              </div>

              {/* Row 2: Search Bar */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl flex items-center border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-gray-900/10 dark:focus-within:ring-white/10 transition-all" data-id="element-3262">
                <div className="pl-4 pr-2" data-id="element-3263">
                  <Search size={16} className="text-gray-400" data-id="element-3264" />
                </div>
                <input className="flex-1 py-3 pr-3 bg-transparent outline-none text-sm placeholder:text-gray-400 text-gray-900 dark:text-white" placeholder="Buscar plato..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3265" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="p-2 mr-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" data-id="element-3266">
                    <X size={14} className="text-gray-400" data-id="element-3267" />
                  </button>}
              </div>

              {/* Row 3: Categories - scrollable pills with arrow buttons */}
              <div className="relative -mx-4" data-id="element-3268">
                {/* Left arrow */}
                {canScrollLeft && <button onClick={() => scrollCategories('left')} className="absolute left-0 top-0 bottom-0 z-20 flex items-center justify-center w-8 bg-gradient-to-r from-gray-50 dark:from-[#050505] to-transparent" data-id="element-3269">
                    <div className="w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center shadow-sm" data-id="element-3270">
                      <ChevronLeft size={12} className="text-gray-600 dark:text-gray-300" data-id="element-3271" />
                    </div>
                  </button>}
                {/* Right arrow */}
                {canScrollRight && <button onClick={() => scrollCategories('right')} className="absolute right-0 top-0 bottom-0 z-20 flex items-center justify-center w-8 bg-gradient-to-l from-gray-50 dark:from-[#050505] to-transparent" data-id="element-3272">
                    <div className="w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center shadow-sm" data-id="element-3273">
                      <ChevronRight size={12} className="text-gray-600 dark:text-gray-300" data-id="element-3274" />
                    </div>
                  </button>}
                <div ref={categoryScrollRef} className="flex gap-1.5 overflow-x-auto px-4 touch-pan-x" style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              } as React.CSSProperties} data-id="element-3275">
                  {visibleCategories.map(cat => {
                  const isActive = activeCategory === cat;
                  return <button key={cat} onClick={() => setActiveCategory(cat)} className={`
                          relative whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold
                          transition-all duration-200 shrink-0 outline-none
                          ${isActive ? 'bg-gray-900 text-white dark:bg-white dark:text-black shadow-sm' : 'bg-white dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 border border-gray-200/80 dark:border-gray-700/60'}
                        `} data-id="element-3276">
                        {cat}
                      </button>;
                })}
                  <div className="w-4 shrink-0" data-id="element-3277" />
                </div>
              </div>
            </div>

            {!isFullyLoaded ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-id="element-3278">
                {[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" data-id="element-3279" />)}
              </div> : visibleItems.length === 0 ? <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-3xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-3280">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3281">
                  <Search size={40} className="text-gray-300" data-id="element-3282" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3283">
                  No encontramos ese plato
                </h3>
                <p className="text-gray-500 max-w-xs mx-auto mb-6" data-id="element-3284">
                  Intenta buscar con otro nombre o cambia la categoría.
                </p>
                <button onClick={() => {
              setSearchTerm('');
              setActiveCategory(visibleCategories[0] || '');
            }} className="text-gray-900 dark:text-white font-bold text-sm hover:underline" data-id="element-3285">
                  Ver todo el menú
                </button>
              </div> : <>
                {/* Desktop Grid View (md+) */}
                <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-3 gap-8" data-id="element-3286">
                  {visibleItems.map((item, index) => <ProductCard key={item.id} item={item} index={index} onAdd={handleAddToCart} isAdding={lastAddedId === item.id} disabled={!canOrder} taxExempt={isTaxExempt} data-id="element-3287" />)}
                </div>

                {/* Mobile List View (< md) */}
                <div className="md:hidden space-y-2.5" data-id="element-3288">
                  {visibleItems.map(item => <CompactProductRow key={item.id} item={item} onAdd={handleAddToCart} onClick={i => setSelectedDetailItem(i)} isAdding={lastAddedId === item.id} disabled={!canOrder} taxExempt={isTaxExempt} cartQty={cart[item.id] || 0} data-id="element-3289" />)}
                </div>
              </>}
          </main>
        </div>
      </div>

      <AnimatePresence data-id="element-3290">
        {cartDetails.totalCount > 0 && canOrder && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="fixed bottom-20 md:bottom-6 left-0 right-0 z-40 flex justify-center px-4 pointer-events-none" data-id="element-3291">
            <button onClick={() => setShowCheckout(true)} className="pointer-events-auto bg-gray-900 dark:bg-white text-white dark:text-black w-full max-w-md p-4 rounded-2xl shadow-2xl flex items-center justify-between hover:scale-[1.02] active:scale-[0.98] transition-transform border border-gray-800 dark:border-gray-200" data-id="element-3292">
              <div className="flex items-center gap-4" data-id="element-3293">
                <div className="bg-gray-800 dark:bg-gray-200 w-12 h-12 rounded-full flex items-center justify-center relative shadow-inner" data-id="element-3294">
                  <ShoppingBag size={20} data-id="element-3295" />
                  <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-gray-900 dark:border-white animate-pulse" data-id="element-3296">
                    {cartDetails.totalCount}
                  </span>
                </div>
                <div className="text-left" data-id="element-3297">
                  <span className="block text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wide" data-id="element-3298">
                    {balanceApplied > 0 && finalTotal === 0 ? 'Del Balance' : balanceApplied > 0 ? 'Con Balance Aplicado' : 'Total Estimado'}
                  </span>
                  <span className="block text-xl font-mono font-bold leading-none" data-id="element-3299">
                    {formatCurrency(balanceApplied > 0 && finalTotal === 0 ? balanceApplied : finalTotal)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 font-bold text-sm bg-white/10 dark:bg-black/5 px-4 py-2 rounded-xl" data-id="element-3300">
                Ver Carrito <ArrowRight size={16} data-id="element-3301" />
              </div>
            </button>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-3302">
        {showCheckout && <CheckoutDrawer onClose={() => setShowCheckout(false)} cartItems={cartDetails.items} totals={{
        subtotal: baseSubtotal,
        tax: taxAmount,
        subsidy: subsidyAmount,
        balanceApplied,
        total: finalTotal
      }} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} onCheckout={handleCheckoutProcess} isProcessing={isProcessing} onUpdateQty={(id: string, d: number) => d > 0 ? addItem(id) : removeItem(id)} onRemove={deleteItem} taxExempt={isTaxExempt} currentBalance={currentBalance} applyBalance={applyBalance} onToggleBalance={setApplyBalance} subsidyEnabled={subsidyEnabled} exceedsBalance={exceedsBalance} data-id="element-3303" />}
      </AnimatePresence>

      {isProcessing && <ProcessingOverlay data-id="element-3304" />}

      <AnimatePresence data-id="element-3305">
        {showConfirmation && confirmedData && <SuccessModal data={confirmedData} onClose={() => {
        setShowConfirmation(false);
        navigate('/employee/my-orders');
      }} data-id="element-3306" />}
      </AnimatePresence>

      <MenuItemOptionsModal menuItem={selectedItemForOptions} isOpen={!!selectedItemForOptions} onClose={() => setSelectedItemForOptions(null)} onConfirm={onConfirmOptions} taxExempt={isTaxExempt} data-id="element-3307" />

      <AnimatePresence data-id="element-3308">
        {selectedDetailItem && <ProductDetailModal item={selectedDetailItem} isOpen={!!selectedDetailItem} onClose={() => setSelectedDetailItem(null)} onAdd={handleAddToCart} taxExempt={isTaxExempt} data-id="element-3309" />}
      </AnimatePresence>
    </div>;
}